import { useEffect, useRef, useState } from 'react'
import './RGBParade.css'

function RGBParade({ videoRef, imageRef, useSMPTE = false, seekTime = 0, is2X = false, onToggle2X }) {
  const canvasRef = useRef(null)
  const containerRef = useRef(null)
  const animationFrameRef = useRef(null)
  const [isActive, setIsActive] = useState(true)
  const [brightness, setBrightness] = useState(15)
  const [channelView, setChannelView] = useState('ALL') // 'R', 'G', 'B', or 'ALL'
  const [rulerY, setRulerY] = useState(null) // Y position of ruler line
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 })
  const lastSeekTimeRef = useRef(seekTime)
  const lastFrameTimeRef = useRef(0)
  const tempCanvasRef = useRef(null)
  const imageBitmapRef = useRef(null)
  const TARGET_FPS = 30
  const FRAME_INTERVAL = 1000 / TARGET_FPS // ~33.33ms per frame

  // Resize canvas to fit container
  useEffect(() => {
    const container = containerRef.current
    const canvas = canvasRef.current
    if (!container || !canvas) return

    const resizeCanvas = () => {
      const rect = container.getBoundingClientRect()
      const headerHeight = 60 // Approximate header height
      const availableHeight = rect.height - headerHeight
      const availableWidth = rect.width - 32 // Account for padding
      
      const newWidth = Math.floor(availableWidth)
      const newHeight = Math.floor(Math.max(200, availableHeight))
      
      // Only update if size changed
      if (canvas.width !== newWidth || canvas.height !== newHeight) {
        canvas.width = newWidth
        canvas.height = newHeight
        setCanvasSize({ width: newWidth, height: newHeight })
      }
    }

    resizeCanvas()
    const resizeObserver = new ResizeObserver(resizeCanvas)
    resizeObserver.observe(container)

    return () => resizeObserver.disconnect()
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    const video = useSMPTE ? null : videoRef.current
    const image = useSMPTE ? imageRef.current : null
    const source = useSMPTE ? image : video
    if (!canvas || !source) return

    // Create reusable temporary canvas
    if (!tempCanvasRef.current) {
      tempCanvasRef.current = document.createElement('canvas')
    }

    // Update last seek time when it changes
    lastSeekTimeRef.current = seekTime

    // Use GPU-accelerated 2D context (default settings optimize for GPU)
    const ctx = canvas.getContext('2d', { 
      alpha: false, // Opaque canvas for better performance
      desynchronized: true, // Allow async rendering
      willReadFrequently: false // Enable GPU acceleration (default)
    })
    const width = canvas.width
    const height = canvas.height

    const drawRGBParade = (currentTime = performance.now()) => {
      // Check if we should update
      const seekChanged = seekTime !== lastSeekTimeRef.current
      const isVideoReady = !useSMPTE && video && video.readyState >= 2 && !video.paused
      const isImageReady = useSMPTE && image && image.complete
      const shouldUpdate = isActive && (isVideoReady || isImageReady || seekChanged)
      
      // Throttle to 30fps - only draw if enough time has passed
      const timeSinceLastFrame = currentTime - lastFrameTimeRef.current
      const shouldDraw = shouldUpdate && (seekChanged || timeSinceLastFrame >= FRAME_INTERVAL)
      
      if (!shouldDraw) {
        animationFrameRef.current = requestAnimationFrame(drawRGBParade)
        return
      }
      
      // Update timestamps
      if (seekChanged) {
        lastSeekTimeRef.current = seekTime
      }
      lastFrameTimeRef.current = currentTime

      // Clear canvas
      ctx.fillStyle = '#000'
      ctx.fillRect(0, 0, width, height)

      // Get source dimensions
      const sourceWidth = useSMPTE ? (image.naturalWidth || 640) : (video.videoWidth || 640)
      const sourceHeight = useSMPTE ? (image.naturalHeight || 360) : (video.videoHeight || 360)
      
      let imageData
      let data
      
      // Try to use ImageBitmap for GPU acceleration (if available)
      if (typeof createImageBitmap !== 'undefined') {
        // Use createImageBitmap for better GPU handling
        const tempCanvas = tempCanvasRef.current
        if (tempCanvas.width !== sourceWidth || tempCanvas.height !== sourceHeight) {
          tempCanvas.width = sourceWidth
          tempCanvas.height = sourceHeight
        }
        
        const tempCtx = tempCanvas.getContext('2d', { 
          alpha: false,
          willReadFrequently: false // GPU-accelerated
        })
        tempCtx.drawImage(source, 0, 0, sourceWidth, sourceHeight)
        
        // Get ImageData (GPU-accelerated read)
        imageData = tempCtx.getImageData(0, 0, sourceWidth, sourceHeight)
        data = imageData.data
      } else {
        // Fallback for older browsers
        const tempCanvas = tempCanvasRef.current
        if (tempCanvas.width !== sourceWidth || tempCanvas.height !== sourceHeight) {
          tempCanvas.width = sourceWidth
          tempCanvas.height = sourceHeight
        }
        const tempCtx = tempCanvas.getContext('2d', { willReadFrequently: false })
        tempCtx.drawImage(source, 0, 0, sourceWidth, sourceHeight)
        imageData = tempCtx.getImageData(0, 0, sourceWidth, sourceHeight)
        data = imageData.data
      }

      // RGB Parade: X-axis = horizontal position, Y-axis = intensity value
      // Three separate sections side by side: Red, Green, Blue (or solo at full width)
      const isSoloView = channelView !== 'ALL'
      const channelWidth = isSoloView ? width : Math.floor(width / 3)
      const channelHeight = height
      const intensityLevels = 256 // 0-255 intensity values
      
      // Use TypedArrays for better performance (GPU-friendly)
      // Histogram structure: histograms[channel][intensity][horizontalPosition]
      const histograms = {
        r: new Array(intensityLevels).fill(0).map(() => new Uint32Array(channelWidth)),
        g: new Array(intensityLevels).fill(0).map(() => new Uint32Array(channelWidth)),
        b: new Array(intensityLevels).fill(0).map(() => new Uint32Array(channelWidth))
      }

      // Process each horizontal position in the image (GPU-optimized with sampling)
      // X-axis in each channel section matches X-axis in image (left to right)
      // Each vertical column shows intensity distribution for that horizontal position
      const sampleStep = Math.max(1, Math.floor(sourceHeight / 150)) // Sample every Nth pixel vertically for performance
      const invSourceWidth = 1 / sourceWidth
      const dataLength = data.length
      
      // Process pixels in batches for better cache performance
      for (let imageX = 0; imageX < sourceWidth; imageX++) {
        // Map image horizontal position to parade horizontal position within each channel
        const scanLineX = Math.floor(imageX * invSourceWidth * channelWidth)
        
        if (scanLineX >= 0 && scanLineX < channelWidth) {
          // Process scan lines (vertical positions) at this horizontal position with sampling
          for (let imageY = 0; imageY < sourceHeight; imageY += sampleStep) {
            const idx = (imageY * sourceWidth + imageX) << 2 // Bit shift for * 4 (faster)
            
            if (idx + 2 < dataLength) {
              // Direct array access (faster than destructuring)
              const r = data[idx]
              const g = data[idx + 1]
              const b = data[idx + 2]

              // Increment histogram: count pixels at this intensity for this horizontal position
              // Direct array access with bounds check
              if (r < intensityLevels) histograms.r[r][scanLineX]++
              if (g < intensityLevels) histograms.g[g][scanLineX]++
              if (b < intensityLevels) histograms.b[b][scanLineX]++
            }
          }
        }
      }

      // Draw RGB Parade: Three separate sections side by side, or solo at full width
      const allChannels = [
        { data: histograms.r, color: '#ff0000', label: 'R', offset: 0 },
        { data: histograms.g, color: '#00ff00', label: 'G', offset: channelWidth },
        { data: histograms.b, color: '#0088ff', label: 'B', offset: channelWidth * 2 }
      ]

      // Filter channels based on view mode and adjust offset for solo view
      const channels = isSoloView 
        ? allChannels.filter(ch => ch.label === channelView).map(ch => ({ ...ch, offset: 0 }))
        : allChannels

      channels.forEach(({ data: histData, color, label, offset }) => {
        // Find max count for this channel for normalization
        let maxCount = 0
        for (let intensity = 0; intensity < intensityLevels; intensity++) {
          for (let scanLineX = 0; scanLineX < channelWidth; scanLineX++) {
            if (histData[intensity][scanLineX] > maxCount) {
              maxCount = histData[intensity][scanLineX]
            }
          }
        }

        // Draw the histogram (GPU-optimized)
        // X-axis: horizontal position (matches image horizontal position)
        // Y-axis: intensity value (vertical, bottom=0/black, top=255/white)
        const brightnessMult = brightness
        const invIntensityLevels = 1 / intensityLevels
        const invMaxCount = maxCount > 0 ? 1 / maxCount : 0
        
        // Pre-calculate color components (parse once)
        const r = parseInt(color.slice(1, 3), 16)
        const g = parseInt(color.slice(3, 5), 16)
        const b = parseInt(color.slice(5, 7), 16)
        
        // Batch drawing operations for better GPU performance
        ctx.fillStyle = color // Set base color once
        ctx.globalAlpha = 1
        
        for (let intensity = 0; intensity < intensityLevels; intensity++) {
          const y = channelHeight - 1 - Math.floor(intensity * invIntensityLevels * channelHeight)
          
          for (let scanLineX = 0; scanLineX < channelWidth; scanLineX++) {
            const count = histData[intensity][scanLineX]
            if (count > 0) {
              // Normalize count and apply brightness
              const alpha = Math.min(1, count * invMaxCount * brightnessMult)
              
              if (alpha > 0.01) { // Skip very transparent pixels
                // Use globalAlpha for better GPU performance
                ctx.globalAlpha = alpha
                ctx.fillRect(offset + scanLineX, y, 1, 1)
              }
            }
          }
        }
        
        // Reset globalAlpha
        ctx.globalAlpha = 1

        // Draw channel label at top of each section
        ctx.fillStyle = '#fff'
        ctx.font = 'bold 14px monospace'
        ctx.textAlign = 'left'
        ctx.fillText(label, offset + 10, 20)
      })

      // Draw grid lines (only for ALL view)
      if (!isSoloView) {
        ctx.strokeStyle = '#333'
        ctx.lineWidth = 1
        // Vertical dividers between channels
        ctx.beginPath()
        ctx.moveTo(channelWidth, 0)
        ctx.lineTo(channelWidth, height)
        ctx.moveTo(channelWidth * 2, 0)
        ctx.lineTo(channelWidth * 2, height)
        ctx.stroke()
      }

      // Draw pixel value reference lines (converted from IRE scale: 0, 7.5, 20, 40, 60, 80, 100)
      const ireLevels = [0, 7.5, 20, 40, 60, 80, 100]
      const keyPixelValues = ireLevels.map(ire => Math.round((ire / 100) * 255))
      ctx.setLineDash([2, 2])

      keyPixelValues.forEach((pixel, index) => {
        const y = height - 1 - Math.floor((pixel / 255) * height)

        if (pixel === 0 || pixel === 255) {
          ctx.strokeStyle = '#888'
          ctx.lineWidth = 1.5
        } else if (ireLevels[index] === 7.5) {
          ctx.strokeStyle = '#ffff00'
          ctx.lineWidth = 2
        } else {
          ctx.strokeStyle = '#555'
          ctx.lineWidth = 1
        }

        ctx.beginPath()
        ctx.moveTo(0, y)
        ctx.lineTo(width, y)
        ctx.stroke()
      })

      ctx.setLineDash([])
      ctx.lineWidth = 1

      // Draw additional IRE lines at 70% opacity (10, 30, 50, 70, 90)
      const additionalIreLevels = [10, 30, 50, 70, 90]
      const additionalPixelValues = additionalIreLevels.map(ire => Math.round((ire / 100) * 255))
      ctx.setLineDash([4, 4])
      ctx.globalAlpha = 0.7
      ctx.strokeStyle = '#555'
      ctx.lineWidth = 1

      additionalPixelValues.forEach(pixel => {
        const y = height - 1 - Math.floor((pixel / 255) * height)
        ctx.beginPath()
        ctx.moveTo(0, y)
        ctx.lineTo(width, y)
        ctx.stroke()
      })

      ctx.setLineDash([])
      ctx.globalAlpha = 1

      // IRE labels on left (main lines)
      ctx.fillStyle = '#666'
      ctx.font = '15px monospace'
      ctx.textAlign = 'left'
      ireLevels.forEach((ire, index) => {
        const pixel = keyPixelValues[index]
        const y = height - 1 - Math.floor((pixel / 255) * height)
        ctx.fillText(`${ire}`, 5, y - 2)
      })

      // IRE labels on left (additional lines at 80% size, 70% opacity)
      ctx.font = '12px monospace'
      ctx.globalAlpha = 0.7
      additionalIreLevels.forEach((ire, index) => {
        const pixel = additionalPixelValues[index]
        const y = height - 1 - Math.floor((pixel / 255) * height)
        ctx.fillText(`${ire}`, 5, y - 2)
      })
      ctx.globalAlpha = 1

      // Draw ruler line if set
      if (rulerY !== null) {
        ctx.strokeStyle = '#ff8800'
        ctx.lineWidth = 1
        ctx.setLineDash([4, 4])
        ctx.beginPath()
        ctx.moveTo(0, rulerY)
        ctx.lineTo(width, rulerY)
        ctx.stroke()
        ctx.setLineDash([])
      }

      animationFrameRef.current = requestAnimationFrame(drawRGBParade)
    }

    drawRGBParade()

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [videoRef, imageRef, useSMPTE, isActive, seekTime, brightness, channelView, rulerY, canvasSize])

  // Handle canvas click to set ruler
  const handleCanvasClick = (e) => {
    const canvas = canvasRef.current
    if (!canvas) return
    
    // Get the canvas bounding rect to account for CSS scaling
    const rect = canvas.getBoundingClientRect()
    const scaleY = canvas.height / rect.height
    
    // Scale the click position to match canvas coordinate system
    const y = (e.clientY - rect.top) * scaleY
    
    if (y >= 0 && y < canvas.height) {
      setRulerY(y)
    }
  }

  return (
    <div className="rgb-parade" ref={containerRef}>
      <div className="rgb-parade-header">
        <h2>RGB Parade</h2>
        <div className="header-controls">
          <div className="channel-selector">
            <button
              className={`channel-button ${channelView === 'ALL' ? 'active' : ''}`}
              onClick={() => setChannelView('ALL')}
            >
              ALL
            </button>
            <button
              className={`channel-button ${channelView === 'R' ? 'active' : ''}`}
              onClick={() => setChannelView('R')}
            >
              R
            </button>
            <button
              className={`channel-button ${channelView === 'G' ? 'active' : ''}`}
              onClick={() => setChannelView('G')}
            >
              G
            </button>
            <button
              className={`channel-button ${channelView === 'B' ? 'active' : ''}`}
              onClick={() => setChannelView('B')}
            >
              B
            </button>
          </div>
          {rulerY !== null && (
            <button
              className="clear-ruler-button"
              onClick={() => setRulerY(null)}
              title="Clear ruler"
            >
              CLEAR RULER
            </button>
          )}
          <label className="brightness-control">
            <span>Brightness:</span>
            <input
              type="range"
              min="0.1"
              max="20"
              step="0.1"
              value={brightness}
              onChange={(e) => setBrightness(parseFloat(e.target.value))}
              className="brightness-slider"
            />
            <span className="brightness-value">{brightness.toFixed(1)}</span>
          </label>
          <button
            className="toggle-button"
            onClick={() => setIsActive(!isActive)}
          >
            {isActive ? 'Pause' : 'Resume'}
          </button>
          {onToggle2X && (
            <button
              className={`toggle-button ${is2X ? 'active' : ''}`}
              onClick={onToggle2X}
              title="2X Width (Desktop only)"
            >
              2X
            </button>
          )}
        </div>
      </div>
      <canvas
        ref={canvasRef}
        className="rgb-parade-canvas"
        onClick={handleCanvasClick}
        style={{ cursor: 'crosshair' }}
      />
      <div className="rgb-parade-info">
        <div className="channel-label red">Red Channel</div>
        <div className="channel-label green">Green Channel</div>
        <div className="channel-label blue">Blue Channel</div>
      </div>
    </div>
  )
}

export default RGBParade

